import { Apartment, Workspaces } from "@mui/icons-material";
import { AlertColor, Box, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { NoWorkspacesOrganizationIcon } from "../../../assets/svg/NoWorkspacesOrganizationIcon";
import { EmptyContainer } from "../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";
import { KeyDriveBodyMin } from "../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { KeyDriveh4Header } from "../../../components/KeydriveComponents/Fonts/Headers/KeyDriveh4Header";
import { KeyDriveSearchBarWithButton } from "../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBarWithButton";
import LoaderComp from "../../../components/Loader/LoaderComp";
import { WorkSpace, WorkspaceUserPermission } from "../../../model/WorkSpaceModel";
import { GetOrganizationWorkspaceAdmTrimName } from "../../../services/OrganizationServices";
import { WorkspaceCard } from "./components/WorkspaceCard/WorkspaceCard";
import { KeyDriveBreadcrumb } from "../../../components/KeydriveComponents/Breadcrumb/KeyDriveBreadcrumb";
import KeydriveSnackbar from "../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import { getAllFavorites } from "../../../services/favoriteServices";
import { useProfile } from "../../../context/ProfileProvider/useProfile";
import { IFavoriteModel } from "../../../model/ItemModel";
import useExtractPlan from "../../../hooks/useExtractPlan";
import { KeydrivePlan } from "../../../model/UserModel";
import { verifyWorkpace } from "../../../services/paywallService";
import { PaywallInformation } from "../../../model/PaywallModel";
import { PaywallModal } from "../../../components/PaywallModal/PaywallModal";
import { MaxWorkspaceIcon } from "../../../assets/icons/MaxWorkspaceIcon";
import { use } from "i18next";

export const WorkspacesPage = () => {

    const { t } = useTranslation();
    const [workspaces, setWorkspaces] = useState<WorkSpace[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [workspaceSearchParam, setWorkspaceSearchParam] = useState<string>("");
    const { nameOrg } = useParams();
    const { id: userId } = useProfile();
    const navigate = useNavigate();
    const [refresh, setRefresh] = useState<boolean>(false);
    const [snackMessage, setSnackMesssage] = useState<string>("");
    const [isOpenSnack, setIsOpenSnack] = useState<boolean>(false);
    const [snackSeverity, setSnackSeverity] = useState<AlertColor>("success");
    const [favorites, setAllFavorites] = useState<IFavoriteModel[]>([]);
    const { keydrivePlan } = useExtractPlan();
    const [paywallInfo, setPaywallInfo] = useState<PaywallInformation>();
    const [paywallOpen, setPaywallOpen] = useState<boolean>(false);

    async function GetAllFavorites() {
        try {
            if (nameOrg) {
                const response = await getAllFavorites(nameOrg, userId!);
                setAllFavorites(response.favoriteUserVO);
            }
        } catch (err: any) {
            setSnackMesssage(err.response.data);
            setIsOpenSnack(true);
            setSnackSeverity("error");
        } finally {
            setLoading(false);
            setRefresh(false);
        }
    }

    async function GetAllWorkspaces() {
        try {
            const response = await GetOrganizationWorkspaceAdmTrimName(
                nameOrg!,
                0,
                999,
                true,
                workspaceSearchParam
            );

            setWorkspaces(response);
        } catch (err: any) {
            setSnackMesssage(err.response.data);
            setIsOpenSnack(true);
            setSnackSeverity("error");
        } finally {
            setLoading(false);
            setRefresh(false);
        }
    }
    
    useEffect(() => {
        if (nameOrg != undefined) 
        {
            setLoading(true);
            GetAllFavorites();
            GetAllWorkspaces();
        }
    }, [nameOrg, workspaceSearchParam, refresh]);

    const handleClose = () => {
        setIsOpenSnack(false);
    };

      async function fetchCanCreateWorkspace() {
        try {
          const resp = await verifyWorkpace(nameOrg!);
          setPaywallInfo(resp);
        } catch (ex: any) {
            setSnackMesssage(ex.response.data);
            setIsOpenSnack(true);
            setSnackSeverity("error");
        }
      }
    

    const countUsers = (permission?: WorkspaceUserPermission, adminUsers?: string[]): number => {
        const allUsers = [
            ...(permission?.adminUsers ?? []),
            ...(permission?.organizeUsers ?? []),
            ...(permission?.commonUsers ?? []),
            ...(permission?.readUsers ?? []),
            ...(adminUsers ?? [])
        ];

        return new Set(allUsers).size;
    };

    return (
        <>        
            <Box sx={{
                padding: '15px 30px',
                backgroundColor: '#F7F8FA',
                height: '100%'
            }}>
            <Box sx={{
                    background: '#FFFFFF 0% 0% no-repeat padding-box',
                    border: '1px solid #0000001A',
                    borderRadius: '4px',
                    padding: '30px',
                    height: 'calc(100% - 20px);'
                }}>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: '20px',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: "row",
                            gap: '12px',
                        }}>
                            <Workspaces sx={{
                                color: "#22638D",
                                alignSelf: 'self-end',
                                width: '27px',
                                height: '27px',
                            }} widths='24px' height='24px'/>
                            <KeyDriveh4Header
                                color="#2D2D2D"
                                needEllipses={false}
                                text="Workspaces"
                                needBold
                            /> 
                        </Box>
                        <Box>
                            <KeyDriveBodyMin 
                                needEllipses={false}
                                color="#969696"
                                text={t('AdminConsole.WorkspaceSubtittle')}
                                needBold={false}
                            />
                        </Box>
                        <Divider />
                        <KeyDriveSearchBarWithButton
                            btnText={t("AdminConsole.NewWorkspace")}
                            onChange={(e) => setWorkspaceSearchParam(e.currentTarget.value)}
                            onClick={() => {
                                verifyWorkpace(nameOrg!)
                                .then(data => {
                                    if (!data?.canDo) {
                                        setPaywallInfo(data);
                                        setPaywallOpen(true);
                                    } else {
                                        navigate(`config`);
                                    }
                                })
                                .catch((ex) => {
                                    setSnackMesssage(ex.response.data);
                                    setIsOpenSnack(true);
                                    setSnackSeverity("error");
                                })
                            }}
                            disabledButton={keydrivePlan == KeydrivePlan.FREE && workspaces.length >= 1}
                        />
                        { workspaces.length <= 0 && !loading ? (
                                <div style={{height: "calc(100vh - 370px)"}}>

                                    <EmptyContainer 
                                        content={t('AdminConsole.NoFoundWorkspaceSubtittle')}
                                        title={t('AdminConsole.NotFoundWorkspace')}
                                        icon={<NoWorkspacesOrganizationIcon />}
                                    />
                                </div>
                            ) : (
                                <>
                                    { loading ? (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            width="100%"
                                        >
                                            <LoaderComp />
                                        </Box>
                                    ) : (
                                        <div style={{
                                            height: "calc(100vh - 370px)",
                                            overflowY: "auto",
                                            background: '#FFFFFF 0% 0% no-repeat padding-box',
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "19px"
                                        }}>
                                            { workspaces.map(({
                                                name,
                                                adminUsers,
                                                permission,
                                                documents,
                                                id,
                                                description
                                            }, index, list) => (
                                                <WorkspaceCard 
                                                    name={name!}
                                                    description={description != undefined ? description : t("AdminConsole.NoDescription")}
                                                    documents={documents!}
                                                    users={countUsers(permission,adminUsers)}
                                                    index={index}
                                                    workspacesLength={list.length}
                                                    id={id!}
                                                    setLoading={setLoading}
                                                    setRefresh={setRefresh}
                                                    favorites={favorites}
                                                    itemId={favorites.filter(x => x.favoriteName == name)[0]?.id!}
                                                />
                                            )) }
                                        </div>
                                    ) }
                                </>
                            ) }
                    </Box>
                </Box>
            </Box>
            <KeydriveSnackbar 
                message={snackMessage}
                open={isOpenSnack}
                variant={snackSeverity}
                onClose={handleClose}
            />

            <PaywallModal
                icon={<MaxWorkspaceIcon />}
                open={paywallOpen}
                setOpen={setPaywallOpen}
                subtitleStart={paywallInfo?.paywallContent!}
                subtitleBold={` ${keydrivePlan}`}
                subtitleEnd={paywallInfo?.paywallTitle!}
                paywallContent={paywallInfo?.paywallContent!}
            />
        </>
    )
}